import type React from "react";
import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { AlertTriangle, Shield, Phone, Lock } from "lucide-react";
import { getTextingNumberActivation } from "@/app/_module/utils/textingNumberActivation";

import { sendSms } from "@/app/(authenticated)/communication/_modules/services/sendSms";
import type { SendSmsRequest } from "@/app/(authenticated)/communication/_modules/types/sendSms.types";
import type { Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { SelectTemplateButton } from "@/app/(authenticated)/settings/_modules/components/SelectTemplateButton";
import type { SmsTemplate } from "@/app/(authenticated)/settings/_modules/types/sms-settings";
import { useSMSQueryInvalidation } from "@/app/_module/hooks/useSMSQueryInvalidation";
import { A2PGateNotice } from "@/app/_module/components/A2PGateNotice";
import { useSmsConversation } from "@/app/_module/contexts/SmsConversationContext";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Label } from "@/app/_module/components/ui/label";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { a2pService } from "@/app/_module/services/a2pService";
import { fillVariables, leadNameVariables } from "@/app/_module/utils/smsVariables";

interface SendSmsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead; // The lead object from the lead id page
}

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

export const SendSmsModal: React.FC<SendSmsModalProps> = function({
  open,
  onOpenChange,
  lead,
}) {
  const router = useRouter();
  const { invalidateSMSQueries } = useSMSQueryInvalidation();
  const { startConversation } = useSmsConversation();
  const { data: phoneNumbers = [] } = useUserPhoneNumbers();
  const { user } = useUserContext();

  const [phone, setPhone] = useState<string>(lead.phone || "");
  const [text, setText] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [a2pStatus, setA2pStatus] = useState<any>(null);
  const [checkingA2P, setCheckingA2P] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();
  const [fromNumber, setFromNumber] = useState<string>("");

  // Sole-prop accounts are locked to a single registered texting number. The carriers tie the
  // approval to that exact number, so sending from any other one comes back "unregistered" and
  // never reaches the lead. Signature: exactly ONE number carries campaignLinkStatus 'linked'
  // (LVS/Standard accounts have every number linked, so they keep the picker).
  const lockedFromNumber = useMemo(() => {
    const linked = (phoneNumbers as any[]).filter((p: any) => p.campaignLinkStatus === 'linked');
    return linked.length === 1 ? linked[0].phoneNumber : null;
  }, [phoneNumbers]);

  // Warn while the carriers are still activating a recently-switched number: sends in that
  // window come back undelivered and the agent has no way to know why.
  const activation = useMemo(() => {
    const lk = (phoneNumbers as any[]).find((p: any) => p?.campaignLinkStatus === 'linked');
    return getTextingNumberActivation(lk?.linkedToCampaignAt);
  }, [phoneNumbers]);

  // Pin it so the locked number always wins over the heuristic pick.
  useEffect(() => {
    if (lockedFromNumber && fromNumber !== lockedFromNumber) {
      setFromNumber(lockedFromNumber);
    }
  }, [lockedFromNumber, fromNumber]);

  // Update phone if lead changes
  useEffect(() => {
    setPhone(lead.phone || "");
  }, [lead]);

  // Check A2P status when modal opens
  useEffect(() => {
    if (open) {
      checkA2PStatus();
    }
  }, [open]);

  // Get user's default outbound phone from profile
  const userDefaultPhone = (user?.user as unknown as Record<string, unknown>)?.defaultOutboundPhone as string;

  // Select best outbound phone — mirrors the backend's priority:
  // 1. A2P-linked (campaign-registered) number wins (for sole prop only one exists; backend
  //    forces swap to this number anyway)
  // 2. User's default outbound phone if set
  // 3. Area code match against the lead's number
  // 4. First available
  const selectBestOutboundPhone = (leadPhone: string): string => {
    if (!phoneNumbers.length) return "";

    const a2pLinkedNumbers = phoneNumbers.filter((p: any) => p.campaignLinkStatus === 'linked');
    if (a2pLinkedNumbers.length === 1) {
      return a2pLinkedNumbers[0].phoneNumber;
    }

    const pool = a2pLinkedNumbers.length > 0 ? a2pLinkedNumbers : phoneNumbers;

    if (userDefaultPhone && pool.some((p: any) => p.phoneNumber === userDefaultPhone)) {
      return userDefaultPhone;
    }

    const leadAreaCode = leadPhone.replace(/\D/g, '').slice(0, 3);
    const matchingPhone = pool.find((phone: any) => {
      const phoneAreaCode = phone.phoneNumber?.replace(/\D/g, '').slice(0, 3);
      return phoneAreaCode === leadAreaCode;
    });
    if (matchingPhone) return matchingPhone.phoneNumber;

    return pool[0].phoneNumber;
  };

  // Sync the from selector to the best default when the lead or phone numbers change
  const bestFromNumber = useMemo(() => selectBestOutboundPhone(lead.phone || ''), [phoneNumbers, userDefaultPhone, lead.phone]);
  useEffect(() => {
    if (!fromNumber && bestFromNumber) {
      setFromNumber(bestFromNumber);
    }
  }, [bestFromNumber, fromNumber]);

  const checkA2PStatus = async () => {
    try {
      setCheckingA2P(true);
      const canSend = await a2pService.canSendSMS();
      setA2pStatus(canSend);
    } catch (error) {
      setA2pStatus({ canSend: false, reason: 'Failed to check A2P verification status' });
    } finally {
      setCheckingA2P(false);
    }
  };

  // Handle template selection with variable substitution
  const handleTemplateSelect = (template: SmsTemplate) => {
    setSelectedTemplateId(template.id);

    // Get the outbound phone number (area code matched or default)
    const outboundPhone = selectBestOutboundPhone(lead.phone || '');

    // Common variables that can be used in templates
    const variables: Record<string, string> = {
      // Basic Info, without the import's " (2)"
      ...leadNameVariables(lead),
      // Contact Info
      email: lead.email ? String(lead.email) : '',
      recipient_phone: lead.phone || '',
      // Agent Info
      phone: outboundPhone, // Your outbound phone (area code matched)
      my_phone: outboundPhone,
      agent_phone: outboundPhone,
      agency_name: (user?.currentAgency?.agency as any)?.name || '',
      // Address
      address: lead.address || '',
      city: lead.city || '',
      state: lead.state || '',
      zip_code: lead.zipCode || '',
      // Personal Details
      age: lead.age ? String(lead.age) : '',
      dob: lead.dob ? String(lead.dob) : '',
      gender: lead.gender ? String(lead.gender) : '',
      marital_status: lead.maritalStatus || (lead.marital ? String(lead.marital) : ''),
    };

    // Any spelling; ones without a value stay for the server to fill at send, or stop.
    setText(fillVariables(template.content, variables));
  };

  const handleSend = async () => {
    if (!phone || !text) {
      return;
    }

    // Check A2P status before sending
    if (!a2pStatus?.canSend) {
      // Show A2P verification required alert and redirect
      return;
    }

    setLoading(true);
    const payload: SendSmsRequest = {
      to: phone,
      text,
      from: fromNumber || undefined,
    };
    try {
      const result = await sendSms(payload);

      if (result) {
        const sentMessage = text; // Store before clearing
        setText("");

        // Invalidate SMS queries to refresh history
        await invalidateSMSQueries();

        // Start SMS conversation with the floating widget (now async - loads history)
        await startConversation({
          leadId: lead.id,
          leadName: lead.name,
          phoneNumber: phone,
          initialMessage: sentMessage,
        });

        onOpenChange(false);
      }
    } catch (error: any) {
      // Check if error is A2P related
      if (error.message?.includes('A2P_VERIFICATION_REQUIRED')) {
        // Refresh A2P status and show redirect
        await checkA2PStatus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleA2PRedirect = () => {
    onOpenChange(false);
    router.push('/settings/a2p-verification');
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <ResponsiveDialogContent size="md">
        <DialogHeader>
          <DialogTitle>Send SMS</DialogTitle>
          <DialogDescription>
            Send a text message to {lead.name} at <span className="font-semibold">{formatPhoneNumber(phone)}</span>
          </DialogDescription>
        </DialogHeader>
        
        {/* A2P Verification Status */}
        {checkingA2P ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Checking SMS permissions...</span>
          </div>
        ) : !a2pStatus?.canSend ? (
          <A2PGateNotice
            stage={a2pStatus?.stage || 'required'}
            reason={a2pStatus?.reason}
            onAction={handleA2PRedirect}
          />
        ) : (
          <div className="space-y-4">
            {/* "Send from" dropdown — pre-selects backend's best pick (A2P-linked first).
                For sole prop agents, only one A2P-linked number exists so the dropdown
                effectively locks to it. Backend still overrides if a non-registered
                number is somehow picked. */}
            {phoneNumbers.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium flex items-center gap-1.5">
                  <Phone className="h-3 w-3" />
                  Send from
                </Label>
                {activation && (
                  <div className="mb-2 p-2 rounded-md bg-blue-50 border border-blue-200">
                    <p className="text-[11px] text-blue-900">
                      <strong>Your texting number is still activating.</strong>{' '}{activation.message}
                    </p>
                  </div>
                )}
                {lockedFromNumber ? (
                  <div className="flex items-center justify-between gap-2 h-9 px-3 rounded-md border bg-muted/40">
                    <span className="text-sm font-medium">{formatPhoneNumber(lockedFromNumber)}</span>
                    <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Lock className="h-3 w-3" />
                      Your registered texting number
                    </span>
                  </div>
                ) : (
                <Select value={fromNumber} onValueChange={setFromNumber}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select a number" />
                  </SelectTrigger>
                  <SelectContent>
                    {phoneNumbers.map((p: any) => {
                      const isA2PLinked = p.campaignLinkStatus === 'linked';
                      const areaCode = p.phoneNumber?.replace(/\D/g, '').slice(0, 3) || '';
                      return (
                        <SelectItem key={p.id || p.phoneNumber} value={p.phoneNumber}>
                          <div className="flex items-center justify-between gap-3 w-full">
                            <span className="font-medium">{formatPhoneNumber(p.phoneNumber)}</span>
                            <div className="flex items-center gap-1.5">
                              {p.state && (
                                <span className="text-xs text-muted-foreground">{p.state}</span>
                              )}
                              {areaCode && !p.state && (
                                <span className="text-xs text-muted-foreground">{areaCode}</span>
                              )}
                              {isA2PLinked && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                                  A2P
                                </span>
                              )}
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                )}
                {phoneNumbers.length > 1 && phoneNumbers.filter((p: any) => p.campaignLinkStatus === 'linked').length === 1 && (
                  <p className="text-xs text-muted-foreground">
                    💡 Sole prop accounts can only send SMS from their A2P-registered number. If you pick a different one, the system will swap to the registered number automatically.
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <SelectTemplateButton
                onTemplateSelect={handleTemplateSelect}
                selectedTemplateId={selectedTemplateId}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Select a template to auto-fill the message. Variables like {`{{name}}`} will be replaced automatically.
              </p>
            </div>
            <Textarea
              required
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your message or select a template above..."
              rows={4}
              value={text}
            />
          </div>
        )}
        
        <DialogFooter>
          {a2pStatus?.canSend ? (
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading || !phone || !text}
              onClick={handleSend}
            >
              {loading ? "Sending..." : "Send SMS"}
            </Button>
          ) : !checkingA2P ? (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full"
            >
              Close
            </Button>
          ) : null}
        </DialogFooter>
      </ResponsiveDialogContent>
    </Dialog>
  );
};